import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, Plus, Pencil, Trash2 } from "lucide-react";

export default function Buyers() {
  const [buyers, setBuyers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", address_block: "" });

  const load = () => api.get("/buyers").then((r) => setBuyers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm({ name: "", address_block: "" }); setOpen(true); };
  const openEdit = (b) => { setEdit(b); setForm({ name: b.name, address_block: b.address_block }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Buyer name required");
    try {
      if (edit) await api.put(`/buyers/${edit.id}`, { ...edit, ...form });
      else await api.post("/buyers", form);
      toast.success(edit ? "Buyer updated" : "Buyer added");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/buyers/${id}`);
    toast.success("Buyer removed");
    load();
  };

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-slate-900" />
          </div>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Buyers Library</h1>
            <p className="text-sm text-slate-500">Save department address blocks to reuse in the "To" section.</p>
          </div>
        </div>
        <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-buyer-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Buyer
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {buyers.map((b) => (
          <Card key={b.id} data-testid={`buyer-${b.id}`} className="p-5 rounded-xl border-border group">
            <div className="flex items-start justify-between">
              <div className="font-head font-bold">{b.name}</div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)} data-testid={`edit-buyer-${b.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(b.id)} data-testid={`delete-buyer-${b.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <pre className="text-xs text-slate-500 mt-2 whitespace-pre-wrap font-sans leading-relaxed">{b.address_block}</pre>
          </Card>
        ))}
        {buyers.length === 0 && <div className="text-sm text-slate-500 col-span-full py-10 text-center">No buyers saved yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit ? "Edit Buyer" : "Add Buyer"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Buyer / Department Name</Label>
              <Input data-testid="buyer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Full Address Block (one line each, as it should appear under "To,")</Label>
              <Textarea data-testid="buyer-address-input" rows={4} value={form.address_block} onChange={(e) => setForm({ ...form, address_block: e.target.value })} className="mt-1.5 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={submit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="save-buyer-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
